import { useCallback, useEffect, useRef, useState } from 'react'
import type { PaginatedResult, SortDirection } from '@ai-meeting/shared'
import { ApiError } from '@/services/api'
import { useUiStrings } from '@/hooks/useUiStrings'

const SEARCH_DEBOUNCE_MS = 300

export interface AdminTableState<TSortBy extends string> {
  page: number
  search: string
  sortBy: TSortBy
  sortDir: SortDirection
}

export interface UseAdminTableOptions<TRow, TSortBy extends string, TExtra> {
  /** Fetches one page. Receives the merged query (paging + sort + extra filters). */
  fetcher: (
    query: AdminTableState<TSortBy> & TExtra & { pageSize: number }
  ) => Promise<PaginatedResult<TRow>>
  defaultSortBy: TSortBy
  defaultSortDir?: SortDirection
  pageSize?: number
  /** Extra filter params (e.g. method, dateFrom, dateTo) merged into the query. */
  filters?: TExtra
  /** When false, fetching is deferred (used for lazy per-tab loading). */
  enabled?: boolean
}

export interface UseAdminTableResult<TRow, TSortBy extends string> {
  rows: TRow[]
  total: number
  page: number
  totalPages: number
  loading: boolean
  error: string | null
  search: string
  sortBy: TSortBy
  sortDir: SortDirection
  setSearch: (value: string) => void
  setPage: (page: number) => void
  toggleSort: (column: TSortBy) => void
  /** Refetches the current page — for tabs that also write, not just read. */
  reload: () => void
}

/**
 * State + data-fetching engine for a single admin table tab. Owns pagination,
 * debounced search, whitelist-driven sort and refetches whenever any of those
 * (or the injected `filters`) change. Fetching is skipped until `enabled` is
 * true so tabs only load when first opened.
 *
 * @returns Current rows/paging state plus setters bound to the table UI.
 */
export function useAdminTable<TRow, TSortBy extends string, TExtra = object>(
  options: UseAdminTableOptions<TRow, TSortBy, TExtra>
): UseAdminTableResult<TRow, TSortBy> {
  const {
    fetcher,
    defaultSortBy,
    defaultSortDir = 'desc',
    pageSize = 10,
    filters,
    enabled = true,
  } = options
  const { t } = useUiStrings()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState<TSortBy>(defaultSortBy)
  const [sortDir, setSortDir] = useState<SortDirection>(defaultSortDir)

  // Bumped by `reload()`. Deliberately not part of the query: the API rejects
  // unknown params (`forbidNonWhitelisted`), so this only re-runs the effect.
  const [reloadToken, setReloadToken] = useState(0)
  const reload = useCallback(() => setReloadToken((token) => token + 1), [])

  const [rows, setRows] = useState<TRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Debounce the raw search input into the query-driving `search` value.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [searchInput])

  // Reset to first page whenever external filters change.
  const filtersKey = JSON.stringify(filters ?? {})
  useEffect(() => {
    setPage(1)
  }, [filtersKey])

  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    setLoading(true)
    setError(null)

    fetcherRef
      .current({
        page,
        search,
        sortBy,
        sortDir,
        pageSize,
        ...(filters ?? ({} as TExtra)),
      })
      .then((result) => {
        if (cancelled) return
        setRows(result.data)
        setTotal(result.total)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setRows([])
        setTotal(0)
        setError(err instanceof ApiError ? err.message : t.adminLoadError)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    enabled,
    page,
    search,
    sortBy,
    sortDir,
    pageSize,
    filtersKey,
    filters,
    reloadToken,
    t.adminLoadError,
  ])

  const toggleSort = useCallback(
    (column: TSortBy) => {
      if (column === sortBy) {
        setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortBy(column)
        setSortDir('asc')
      }
      setPage(1)
    },
    [sortBy]
  )

  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 1

  return {
    rows,
    total,
    page,
    totalPages,
    loading,
    error,
    search: searchInput,
    sortBy,
    sortDir,
    setSearch: setSearchInput,
    setPage,
    toggleSort,
    reload,
  }
}
